import { useState, useEffect, useMemo } from "react";
import { v4 as uuid } from "uuid";
import { useParams, useSearchParams } from "react-router-dom";
import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";
import { getQualityMeta, getQualityMetaList, rejectQualityMeta, approveQualityMeta } from "../../../api/quality/meta";
import PeopleCellLoader from "../../../components/common/people/PeopleCellLoader";
import { addApprovalLine } from "../../../api/approval";

/* ✅ 드롭다운 옵션 컴포넌트 */
const ResultOptions = () => (
  <>
    <option value="">선택</option>
    <option value="OK">적합</option>
    <option value="NG">부적합</option>
    <option value="NA">해당없음</option>
  </>
);

export default function CheckList_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const [searchParams] = useSearchParams();

  /* ===============================
     1. 로그인 유저 & 기본 설정
  =============================== */
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const myId = String(user.id || "");

  const [dns, setDns] = useState(paramDns || undefined);

  /* ===============================
     2. MI 연결 데이터
  =============================== */
  const initMiDns = searchParams.get("mi_dns") || "";
  const [miDns, setMiDns] = useState(initMiDns);
  const [miData, setMiData] = useState(null);
  const [miList, setMiList] = useState([]);

  /* ===============================
     3. 권한 및 담당자
  =============================== */
  const officialMeta = miData?.meta?.officialMeta || {};

  const constructorId = String(officialMeta?.site_agent?.id || "");
  const currentPmId = String(officialMeta?.manager?.id || "");

  const canCheckConstructor =
    myId && (myId === constructorId || !constructorId);
  const canCheckPM = myId && myId === currentPmId;

  const canAssignNext = !currentPmId || myId === constructorId;
  const [nextAssignee, setNextAssignee] = useState([]);

  /* ===============================
     4. 문서 기본 정보
  =============================== */
  const [title, setTitle] = useState("검측 체크리스트");
  const [writer, setWriter] = useState("");
  const [writeDate, setWriteDate] = useState("");
  const [checkDate, setCheckDate] = useState("");

  const [revDate, setRevDate] = useState("");
  const [revNo, setRevNo] = useState("");
  const [docNo, setDocNo] = useState("");

  const [workType, setWorkType] = useState("");
  const [codeNo, setCodeNo] = useState("");
  const [locationName, setLocationName] = useState("");
  const [locationPlace, setLocationPlace] = useState("");
  const [lotNo, setLotNo] = useState("");
  const [applyRegulation, setApplyRegulation] = useState("");
  const [itpNo, setItpNo] = useState("");
  const [pageLeft, setPageLeft] = useState("");
  const [pageRight, setPageRight] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    setWriter(user.name || "");
    setWriteDate(new Date().toISOString().slice(0, 10));
    setCheckDate(new Date().toISOString().slice(0, 10));
  }, [user]);

  /* ===============================
     5. MI 목록 로드
  =============================== */
  useEffect(() => {
    (async () => {
      try {
        const res = await getQualityMetaList({
          project: projectId,
          "__client__.form_type": "MI",
        });
        const list = Array.isArray(res?.results) ? res.results : [];
        setMiList(list);
      } catch {
        setMiList([]);
      }
    })();
  }, [projectId]);

  const loadMiDoc = async (dnsValue) => {
    if (!dnsValue) {
      setMiDns("");
      setMiData(null);
      return;
    }
    try {
      const res = await getQualityMeta({ dns: dnsValue });
      setMiDns(dnsValue);
      setMiData(res);
    } catch {
      setMiData(null);
    }
  };

  useEffect(() => {
    if (initMiDns) loadMiDoc(initMiDns);
  }, [initMiDns]);

  useEffect(() => {
    if (!miData) return;
    const h = miData?.header || {};
    if (!locationName && h.location_name) setLocationName(h.location_name);
    if (!locationPlace && h.location_place) setLocationPlace(h.location_place);
    if (!workType && h.work_type) setWorkType(h.work_type);
    if (!codeNo && h.code_no) setCodeNo(h.code_no);
  }, [miData, locationName, locationPlace, workType, codeNo]);

  /* ===============================
     6. 체크리스트 행
  =============================== */
  const makeRow = () => ({
    _id: uuid(),
    item: "",
    criteria: "",
    criteria_type: "",
    s_result_1: "",
    s_result_2: "",
    pm_result_1: "",
    pm_result_2: "",
    action: "",
  });

  const [rows, setRows] = useState(Array.from({ length: 10 }, makeRow));

  const updateCell = (id, key, value) =>
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [key]: value } : r))
    );

  const addRow = () => setRows((p) => [...p, makeRow()]);
  const removeRow = (id) =>
    setRows((p) => (p.length > 1 ? p.filter((r) => r._id !== id) : p));

  /* ===============================
     7. 저장 바디 (🔥 여기만 핵심 수정)
  =============================== */
  const getBody = () => {
    const cleanLines = rows
      .filter((r) => r.item || r.criteria)
      .map(({ _id, ...rest }) => rest);

    const finalConstructorId = constructorId
      ? Number(constructorId)
      : myId
        ? Number(myId)
        : null;

    const finalPmId = nextAssignee?.[0]?.id
      ? Number(nextAssignee[0].id)
      : currentPmId
        ? Number(currentPmId)
        : null;

    const assignedPeople = [];
    if (finalConstructorId) assignedPeople.push(finalConstructorId);
    if (finalPmId) assignedPeople.push(finalPmId);

    return {
      values: {
        meta: {
          project: projectId,
          module: "quality",
          doc_type: searchParams.get("doc_type") || "PC",
          __client__: { form_type: searchParams.get("doc_type") || "PC" },
          mi_dns: miDns,
          title,
          header: {
            writer,
            write_date: writeDate,
            check_date: checkDate,
            mi_dns: miDns,
            work_type: workType,
            code_no: codeNo,
            location_name: locationName,
            location_place: locationPlace,
            lot_no: lotNo,
            apply_regulation: applyRegulation,
            itp_no: itpNo,
            rev_date: revDate,
            rev_no: revNo,
            doc_no: docNo,
            page_left: pageLeft,
            page_right: pageRight,
            note,
            assigned_people: assignedPeople,

            /* ✅ 여기 */
            assigned_people_info: {
              constructor: finalConstructorId
                ? {
                  id: finalConstructorId,
                  name: officialMeta?.site_agent?.name || "",
                  company: officialMeta?.site_agent?.company || "",
                  position: officialMeta?.site_agent?.position || "",
                  sign: officialMeta?.site_agent?.sign || null,
                }
                : null,
              pm: finalPmId
                ? {
                  id: finalPmId,
                  name:
                    nextAssignee?.[0]?.name ||
                    officialMeta?.manager?.name ||
                    "",
                  company:
                    nextAssignee?.[0]?.company ||
                    officialMeta?.manager?.company ||
                    "",
                  position:
                    nextAssignee?.[0]?.position ||
                    officialMeta?.manager?.position ||
                    "",
                  sign: officialMeta?.manager?.sign || null,
                }
                : null,
            },
          },
          lines: cleanLines,
          level2: "APR",
        },
      },
    };
  };

  /* ===============================
     8. 저장 후 3가지 케이스 처리
  =============================== */
  const handleOnSaved = async ({ dns: savedDns }) => {
    if (!miDns) {
      console.warn("[CheckList] MI DNS가 없습니다.");
      return;
    }

    try {
      // --- 1) 결재 결과 분석 ---
      const hasConstructorNG = rows.some((r) => r.s_result_1 === "NG");
      const hasConstructorOK = rows.some((r) => r.s_result_1 === "OK");
      const hasPmNG = rows.some((r) => r.pm_result_1 === "NG");
      const hasPmOK = rows.some((r) => r.pm_result_1 === "OK");

      // 기록된 항목들이 모두 결재되었는지 확인
      const filledRows = rows.filter(
        (r) => (r.item && r.item.trim()) || (r.criteria && r.criteria.trim())
      );
      const allConstructorFilled = filledRows.every(
        (r) => r.s_result_1 && r.s_result_1 !== ""
      );
      const allPmFilled = filledRows.every(
        (r) => r.pm_result_1 && r.pm_result_1 !== ""
      );

      console.log("[CheckList] 결재 상태:", {
        hasConstructorNG,
        hasConstructorOK,
        hasPmNG,
        hasPmOK,
        allConstructorFilled,
        allPmFilled,
      });

      // --- 2) Case별 처리 ---

      // Case 1: 시공사 1차 부적합
      if (hasConstructorNG) {
        console.log("[CheckList] Case 1: 시공사 1차 부적합");

        // MI 반려
        await rejectQualityMeta(miDns, {
          reason: "검측 체크리스트에서 시공사 1차 부적합 판정",
          pc_dns: savedDns,
        });

        // PC 반려
        await rejectQualityMeta(savedDns, {
          reason: "시공사 1차 부적합",
          pc_dns: savedDns,
        });

        console.log(`[CheckList] MI(${miDns}), PC(${savedDns}) 반려 완료`);
        return;
      }

      // Case 2: PM 1차 부적합 (시공사는 적합)
      if (hasConstructorOK && hasPmNG) {
        console.log("[CheckList] Case 2: PM 1차 부적합");

        // MI 반려
        await rejectQualityMeta(miDns, {
          reason: "검측 체크리스트에서 PM 1차 부적합 판정",
          pc_dns: savedDns,
        });

        // PC 반려
        await rejectQualityMeta(savedDns, {
          reason: "PM 1차 부적합",
          pc_dns: savedDns,
        });

        console.log(`[CheckList] MI(${miDns}), PC(${savedDns}) 반려 완료`);
        return;
      }

      // Case 3: 모두 적합 (시공사 OK && PM OK && 모든 항목 입력 완료)
      if (
        hasConstructorOK &&
        hasPmOK &&
        allConstructorFilled &&
        allPmFilled &&
        !hasConstructorNG &&
        !hasPmNG
      ) {
        console.log("[CheckList] Case 3: 모두 적합");

        // MI 승인
        await approveQualityMeta(miDns, {
          comment: "검측 체크리스트 승인 완료",
          pc_dns: savedDns,
        });

        // PC 승인
        await approveQualityMeta(savedDns, {
          comment: "검측 완료",
          pc_dns: savedDns,
        });

        console.log(`[CheckList] MI(${miDns}), PC(${savedDns}) 승인 완료`);
        return;
      }

      // 아직 결재가 진행 중인 경우
      console.log("[CheckList] 아직 모든 결재가 완료되지 않았습니다.");

      // --- 3) PM 결재선 추가 (기존 로직) ---
      const pmIdForApproval = nextAssignee?.[0]?.id || currentPmId;
      if (savedDns && pmIdForApproval) {
        await addApprovalLine(
          {
            doc_dns: savedDns,
            approver_id: Number(pmIdForApproval),
            order: 1,
            refresh: true,
          },
          projectId
        );
      }
    } catch (err) {
      console.error("[CheckList] 결재 처리 실패:", err);
    }
  };

  return (
    <FormContentManager
      params={{ module: "quality", project: projectId, dns }}
      getBody={getBody}
      onChangeId={setDns}
      redirectTo={`/project/${projectId}/quality/list/checklist`}
      docName="검측 체크리스트"
      docDescribe="검측 항목을 작성하고 PM 담당자를 지정하여 승인 요청을 진행하세요."
      onSaved={handleOnSaved}
    >
      {/* UI 내용 (타이틀, 테이블 등) */}
      <div className="text-center mb-6">
        <div className="text-xl font-bold">{title}</div>
      </div>

      <table className="doc-table w-full text-sm mb-4">
        <tbody>
          <tr>
            <TableCell
              label="작성자"
              editing
              input={
                <input
                  readOnly
                  className="input-base bg-gray-100"
                  value={writer}
                />
              }
            />
            <TableCell
              label="작성일자"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={writeDate}
                  onChange={(e) => setWriteDate(e.target.value)}
                />
              }
            />
          </tr>
          <tr>
            <TableCell
              label="MI 요청서 연결"
              colSpan={3}
              editing
              input={
                <select
                  className="input-base text-xs"
                  value={miDns || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setMiDns(val);
                    if (val) loadMiDoc(val);
                    else setMiData(null);
                  }}
                >
                  <option value="">MI 요청서 선택</option>
                  {miList.map((m) => (
                    <option key={m.doc_dns} value={m.doc_dns}>
                      {m.doc_dns}
                    </option>
                  ))}
                </select>
              }
            />
          </tr>
          <tr>
            <TableCell
              label="제목"
              colSpan={3}
              editing
              input={
                <input
                  className="input-base"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>

      {/* 상세 정보 테이블 */}
      <table className="doc-table w-full text-sm mb-6">
        <tbody>
          <tr>
            <TableCell
              label="분야"
              editing
              input={
                <input
                  className="input-base w-full"
                  value={workType}
                  onChange={(e) => setWorkType(e.target.value)}
                />
              }
            />
            <TableCell
              label="Rev. Date / NO"
              editing
              input={
                <div className="flex space-x-2">
                  <input
                    type="date"
                    className="input-base"
                    value={revDate}
                    onChange={(e) => setRevDate(e.target.value)}
                  />
                  <input
                    className="input-base"
                    value={revNo}
                    onChange={(e) => setRevNo(e.target.value)}
                  />
                </div>
              }
            />
          </tr>
          <tr>
            <TableCell
              label="공종"
              editing
              input={
                <input
                  className="input-base w-full"
                  value={codeNo}
                  onChange={(e) => setCodeNo(e.target.value)}
                />
              }
            />
            <TableCell
              label="Page"
              editing
              input={
                <div className="flex items-center space-x-2 w-full">
                  <input
                    className="input-base w-full"
                    value={pageLeft}
                    onChange={(e) => setPageLeft(e.target.value)}
                  />
                  <span className="text-gray-500">/</span>
                  <input
                    className="input-base w-full"
                    value={pageRight}
                    onChange={(e) => setPageRight(e.target.value)}
                  />
                </div>
              }
            />
          </tr>
          <tr>
            <TableCell
              label="현장명"
              editing
              input={
                <input
                  className="input-base w-full"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                />
              }
            />
            <TableCell
              label="위치/장소"
              editing
              input={
                <input
                  className="input-base w-full"
                  value={locationPlace}
                  onChange={(e) => setLocationPlace(e.target.value)}
                />
              }
            />
          </tr>
          <tr>
            <TableCell
              label="도면번호"
              editing
              input={
                <input
                  className="input-base w-full"
                  value={lotNo}
                  onChange={(e) => setLotNo(e.target.value)}
                />
              }
            />
            <TableCell
              label="ITP NO."
              editing
              input={
                <input
                  className="input-base w-full"
                  value={itpNo}
                  onChange={(e) => setItpNo(e.target.value)}
                />
              }
            />
          </tr>
          <tr>
            <TableCell
              label="점검일자"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                />
              }
            />
            <TableCell
              label="적용 규정"
              editing
              input={
                <input
                  className="input-base w-full"
                  value={applyRegulation}
                  onChange={(e) => setApplyRegulation(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>

      {/* PM 담당자 선택 */}
      {canAssignNext && (
        <table className="doc-table w-full text-sm mb-2">
          <tbody>
            <tr>
              <TableCell
                label="승인 담당자(PM)"
                colSpan={3}
                editing
                input={
                  <PeopleCellLoader
                    single
                    dns={miDns}
                    value={nextAssignee}
                    onChange={setNextAssignee}
                    placeholder="이곳을 클릭하여 승인할 PM을 선택하세요."
                  />
                }
              />
            </tr>
          </tbody>
        </table>
      )}

      {/* 체크리스트 테이블 */}
      <div className="text-body-bold mb-2">검사 항목</div>
      <table className="doc-table w-full text-sm">
        <thead className="doc-thead">
          <tr>
            <th className="doc-th" rowSpan={3}>
              검사항목
            </th>
            <th className="doc-th" rowSpan={3}>
              검사기준
            </th>
            <th className="doc-th" colSpan={5}>
              결과 (Results)
            </th>
            <th className="doc-th" rowSpan={3}>
              비고 (Remark)
            </th>
            <th className="doc-th-end" rowSpan={3} />
          </tr>
          <tr>
            <th className="doc-th-result">구분</th>
            <th className="doc-th-result" colSpan={2}>
              시공사 (Constructor)
            </th>
            <th className="doc-th-result" colSpan={2}>
              PM (Manager)
            </th>
          </tr>
          <tr>
            <th className="doc-th-result" style={{ width: "60px" }}>
              타입
            </th>
            <th className="doc-th-result" style={{ width: "80px" }}>
              1차
            </th>
            <th className="doc-th-result" style={{ width: "80px" }}>
              2차
            </th>
            <th className="doc-th-result" style={{ width: "80px" }}>
              1차
            </th>
            <th className="doc-th-result" style={{ width: "80px" }}>
              2차
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r._id}>
              <td className="doc-td p-0">
                <textarea
                  className="input-sm px-2 py-1 w-full"
                  value={r.item}
                  onChange={(e) => updateCell(r._id, "item", e.target.value)}
                />
              </td>
              <td className="doc-td p-0">
                <textarea
                  className="input-sm px-2 py-1 w-full"
                  value={r.criteria}
                  onChange={(e) =>
                    updateCell(r._id, "criteria", e.target.value)
                  }
                />
              </td>
              <td className="doc-td text-center p-1">
                <select
                  className="w-full text-xs py-1 border rounded text-center"
                  value={r.criteria_type}
                  onChange={(e) =>
                    updateCell(r._id, "criteria_type", e.target.value)
                  }
                >
                  <option value="">선택</option>
                  <option value="SPEC">시방</option>
                  <option value="DRAWING">도면</option>
                </select>
              </td>
              <td className="doc-td text-center p-1">
                <select
                  className={`w-full text-xs py-1 border rounded text-center ${r.s_result_1 === "NG"
                      ? "text-red-600 font-bold"
                      : r.s_result_1 === "OK"
                        ? "text-blue-600"
                        : ""
                    }`}
                  value={r.s_result_1}
                  disabled={!canCheckConstructor}
                  onChange={(e) =>
                    updateCell(r._id, "s_result_1", e.target.value)
                  }
                >
                  <ResultOptions />
                </select>
              </td>
              <td className="doc-td text-center p-1">
                <select
                  className={`w-full text-xs py-1 border rounded text-center ${r.s_result_2 === "NG"
                      ? "text-red-600 font-bold"
                      : r.s_result_2 === "OK"
                        ? "text-blue-600"
                        : ""
                    }`}
                  value={r.s_result_2}
                  disabled={!canCheckConstructor || r.pm_result_1 !== "NG"}
                  onChange={(e) =>
                    updateCell(r._id, "s_result_2", e.target.value)
                  }
                >
                  <ResultOptions />
                </select>
              </td>
              <td className="doc-td text-center p-1">
                <select
                  className={`w-full text-xs py-1 border rounded text-center ${r.pm_result_1 === "NG"
                      ? "text-red-600 font-bold"
                      : r.pm_result_1 === "OK"
                        ? "text-blue-600"
                        : ""
                    }`}
                  value={r.pm_result_1}
                  disabled={!canCheckPM}
                  onChange={(e) =>
                    updateCell(r._id, "pm_result_1", e.target.value)
                  }
                >
                  <ResultOptions />
                </select>
              </td>
              <td className="doc-td text-center p-1">
                <select
                  className={`w-full text-xs py-1 border rounded text-center ${r.pm_result_2 === "NG"
                      ? "text-red-600 font-bold"
                      : r.pm_result_2 === "OK"
                        ? "text-blue-600"
                        : ""
                    }`}
                  value={r.pm_result_2}
                  disabled={!canCheckPM}
                  onChange={(e) =>
                    updateCell(r._id, "pm_result_2", e.target.value)
                  }
                >
                  <ResultOptions />
                </select>
              </td>
              <td className="doc-td p-0">
                <textarea
                  className="input-sm px-2 py-1 w-full"
                  value={r.action}
                  onChange={(e) => updateCell(r._id, "action", e.target.value)}
                />
              </td>
              <td className="doc-td text-center">
                <button className="doc-delete" onClick={() => removeRow(r._id)}>
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 하단 버튼 */}
      <div className="mt-6">
        <div className="text-body-bold mb-2">기타사항</div>
        <textarea
          className="input-base border-black w-full min-h-[100px]"
          placeholder="기타 특이사항, 비고, 전달사항 등을 입력하세요."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <div className="mt-2">
        <button className="btn-basic-sm" onClick={addRow}>
          + 검사 항목 추가
        </button>
      </div>
    </FormContentManager>
  );
}
